import type { NavigationItemIcon } from '@unifyapps/defs/types/interface';
import { getIconFromRegistry } from '@unifyapps/icons/utils/getIconFromRegistry';
import { IconButton } from '@unifyapps/ui/components/IconButton';
import { UIIcon } from '@unifyapps/ui/components/UIIcon';
import type { IconComponentType } from '@unifyapps/ui/types';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import InlineSVG from 'react-inlinesvg';
import { NavigationItemSelectionVariantEnum } from '../types';

export type NavIconProps = {
  className?: string;
  label?: string;
  Icon?: IconComponentType;
  iconUrl?: string;
  iconName?: string;
  selectionVariant?: NavigationItemSelectionVariantEnum;
  isActive?: boolean;
  disabled?: boolean;
  size?: NavigationItemIcon['size'];
  isHorizontalUpfront?: boolean;
  iconClassName?: string;
  hideIcon?: boolean;
  // paints the icon (and, in the Solid IconButton variant, the button) red — used for the builder
  // DevTools error signal. Kept a plain boolean so callers don't need the design-system color union.
  isError?: boolean;
};

function NavigationIcon(props: NavIconProps) {
  const {
    className,
    label,
    Icon,
    iconUrl,
    iconName,
    iconClassName,
    selectionVariant = NavigationItemSelectionVariantEnum.Stroke,
    isActive = false,
    disabled,
    size,
    isHorizontalUpfront,
    hideIcon,
    isError = false,
  } = props;

  if (hideIcon) {
    return null;
  }

  // error → soft danger button, but only while inactive; the active item keeps its selected (filled)
  // bg and we force the icon red below so the error still reads on top of it. The forced red icon
  // (below) is what actually overrides the nav's pinned `svg.ua-side-nav-item-*` color.
  let buttonColor: 'danger' | 'brand' | undefined;
  if (isError && !isActive) buttonColor = 'danger';
  else if (isActive) buttonColor = 'brand';

  // when isHorizontalUpfront is true,then even if selectionVariant is Solid,
  // we dont show the icon button
  if (
    selectionVariant === NavigationItemSelectionVariantEnum.Solid &&
    (iconName || Icon) &&
    !isHorizontalUpfront
  ) {
    const NavIcon = iconName ? getIconFromRegistry(iconName) : undefined;
    const IconComponent = NavIcon ? NavIcon : undefined;

    const iconButtonStyles = {
      ...(disabled ? { cursor: 'not-allowed' } : {}),
      border: 'none',
    };

    return (
      <IconButton
        aria-label={label}
        className={clsx('', {
          'ua-side-nav-iconbutton-active': isActive,
        })}
        color={buttonColor}
        disabled={disabled}
        Icon={Icon ?? IconComponent}
        // !important beats the nav's pinned `svg.ua-side-nav-item-*` color so the error icon is red
        iconClassName={clsx(iconClassName, { '!text-icon-error': isError })}
        size={size ?? 'md'}
        style={iconButtonStyles}
        variant={isActive ? 'solid' : 'soft'}
      />
    );
  } else if (Icon) {
    return (
      <Icon
        aria-hidden={!label}
        aria-label={label}
        className={clsx('shrink-0', className, { '!text-icon-error': isError })}
      />
    );
  } else if (iconName) {
    return (
      <UIIcon
        aria-hidden={!label}
        aria-label={label}
        className={clsx('shrink-0', className, { '!text-icon-error': isError })}
        name={iconName}
      />
    );
  }

  let el: ReactNode = null;

  if (typeof iconUrl === 'string' && iconUrl.endsWith('.svg')) {
    el = (
      <InlineSVG
        aria-hidden={!label}
        aria-label={label}
        className={clsx('shrink-0', className)}
        src={iconUrl}
        title={label}
      />
    );
  }

  if (typeof iconUrl === 'string') {
    el = (
      <img
        alt={label}
        aria-hidden={!label}
        aria-label={label}
        className={clsx('shrink-0', className)}
        src={iconUrl}
      />
    );
  }

  if (selectionVariant === NavigationItemSelectionVariantEnum.Solid && el) {
    return (
      <IconButton
        aria-label={label}
        color={isActive ? 'brand' : undefined}
        IconNode={el}
        size="md"
        variant={isActive ? 'solid' : 'soft'}
      />
    );
  }

  return el;
}

export { NavigationIcon };
